<template>
  <div class="informe-uso-container">
    <CardPrime class="informe-uso-card">
      <template #title>
        <div class="card-header">
          <h3 class="card-title">Top 20 Alumnos por Cumplimiento en Prácticas</h3>
        </div>
      </template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="form-field">
            <label for="colegio-select" class="block text-sm font-medium text-gray-700">Seleccionar Colegio:</label>
            <ColegioDropdown
              v-model="selectedColegio"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona un Colegio'"
              :error="errors.colegio"
              :options="colegios"
              optionLabel="nombre"
              optionValue="id_colegio"
              placeholder="Selecciona un colegio"
              @update:modelValue="cargarCursosYAsignaturas"
            />
            <small v-if="mostrarErrores && errors.colegio" class="p-error">{{ errors.colegio }}</small>
          </div>

          <div class="form-field">
            <label for="cursos-select" class="block text-sm font-medium text-gray-700">Seleccionar Cursos:</label>
            <CursoDropdown
              v-model="selectedCursos"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona uno o más cursos'"
              :error="errors.cursos"
              :options="cursos"
              :multiple="true"
              optionLabel="nombre"
              optionValue="id_curso"
              placeholder="Selecciona uno o más cursos"
              :disabled="!selectedColegio"
              @update:modelValue="onCursoChange"
            />
            <small v-if="mostrarErrores && errors.cursos" class="p-error">{{ errors.cursos }}</small>
          </div>

          <div class="form-field">
            <label for="asignaturas-select" class="block text-sm font-medium text-gray-700">Seleccionar Asignaturas:</label>
            <AsignaturaDropdown
              v-model="selectedAsignaturas"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona una o más asignaturas'"
              :error="errors.asignaturas"
              :options="asignaturas"
              :multiple="true"
              optionLabel="glosa"
              optionValue="id_asignatura"
              placeholder="Selecciona una o más asignaturas"
              :disabled="!selectedColegio"
              @update:modelValue="onAsignaturaChange"
            />
            <small v-if="mostrarErrores && errors.asignaturas" class="p-error">{{ errors.asignaturas }}</small>
          </div>

          <div class="form-field">
            <label for="inicio" class="block text-sm font-medium text-gray-700">Fecha Inicio:</label>
            <input
              type="date"
              id="inicio"
              v-model="inicio"
              class="p-inputtext w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>

          <div class="form-field">
            <label for="fin" class="block text-sm font-medium text-gray-700">Fecha Fin:</label>
            <input
              type="date"
              id="fin"
              v-model="fin"
              class="p-inputtext w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>
        </div>

        <ButtonPrime
          label="Generar Informe"
          @click="generarInformeTopAlumnos"
          class="mb-4"
          :disabled="loading"
        />

        <div v-if="topAlumnos.length > 0" class="mt-4">
          <div class="flex justify-end mb-2">
            <ButtonPrime
              label="Exportar a Excel"
              icon="pi pi-file-excel"
              class="p-button-success"
              @click="exportToExcel"
            />
          </div>
          <h4 class="text-lg font-semibold mb-2">Top 20 Alumnos por Cumplimiento:</h4>
          <DataTable :value="topAlumnos" class="p-datatable-sm" dataKey="id_usuario">
            <ColumnPrime field="id_usuario" header="ID Usuario" sortable />
            <ColumnPrime field="nombre_completo" header="Nombre Completo" sortable />
            <ColumnPrime field="total_preguntas" header="Total Preguntas" sortable />
            <ColumnPrime field="correctas" header="Correctas" sortable />
            <ColumnPrime field="incorrectas" header="Incorrectas" sortable />
            <ColumnPrime field="porcentaje_cumplimiento" header="Cumplimiento (%)" sortable>
              <template #body="slotProps">
                {{ slotProps.data.porcentaje_cumplimiento.toFixed(2) }}%
              </template>
            </ColumnPrime>
          </DataTable>
        </div>
        <div
          v-else-if="topAlumnos.length === 0 && !loading && wasGenerated"
          class="mt-4 p-4 bg-yellow-100 text-yellow-700 rounded"
        >
          No se encontraron datos para los filtros seleccionados.
        </div>

        <ToastPrime />
      </template>
    </CardPrime>
  </div>
</template>

<script>
import usuarioService from '@/services/UsuarioService';
import CardPrime from 'primevue/card';
import ButtonPrime from 'primevue/button';
import DataTable from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import ToastPrime from 'primevue/toast';
import CursoDropdown from '@/components/selectores/CursoDropdown.vue';
import AsignaturaDropdown from '@/components/selectores/AsignaturaDropdown.vue';
import ColegioDropdown from '@/components/selectores/ColegioDropdown.vue';
import * as XLSX from 'xlsx';

export default {
  components: {
    CardPrime,
    ButtonPrime,
    DataTable,
    ColumnPrime,
    ToastPrime,
    CursoDropdown,
    AsignaturaDropdown,
    ColegioDropdown,
  },
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      selectedColegio: '',
      selectedCursos: [],
      selectedAsignaturas: [],
      colegios: [],
      cursos: [],
      asignaturas: [],
      inicio: firstDay.toISOString().split('T')[0], // e.g., '2025-06-01'
      fin: lastDay.toISOString().split('T')[0], // e.g., '2025-06-30'
      topAlumnos: [],
      loading: false,
      wasGenerated: false,
      errors: {
        colegio: '',
        cursos: '',
      },
      mostrarErrores: false,
    };
  },
  methods: {
    async loadColegios() {
      try {
        this.loading = true;
        const response = await usuarioService.getColegios();
        this.colegios = response.data || [];
        if (this.colegios.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron colegios.',
            life: 3000,
          });
        }
      } catch (error) {
        console.error('Error al cargar colegios:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudieron cargar los colegios.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async cargarCursosYAsignaturas() {
      this.selectedCursos = [];
      this.selectedAsignaturas = [];
      this.cursos = [];
      this.asignaturas = [];

      if (!this.selectedColegio) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'Por favor, selecciona un colegio.',
          life: 3000,
        });
        return;
      }

      this.loading = true;
      try {
        // Cargar cursos
        const cursosResponse = await usuarioService.getCursosPorColegio(this.selectedColegio);
        this.cursos = cursosResponse.data || [];
        if (this.cursos.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron cursos para el colegio seleccionado.',
            life: 3000,
          });
        }

        // Cargar asignaturas
        const asignaturasResponse = await usuarioService.getAsignaturasPorColegio(this.selectedColegio);
        this.asignaturas = asignaturasResponse.data || [];
        if (this.asignaturas.length === 0) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron asignaturas para el colegio seleccionado.',
            life: 3000,
          });
        }
      } catch (error) {
        console.error('Error al cargar cursos o asignaturas:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudieron cargar los cursos o asignaturas.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    async generarInformeTopAlumnos() {
      this.mostrarErrores = false;
      this.wasGenerated = true;

      // Validar inputs
      if (!this.selectedColegio) {
        this.errors.colegio = 'Debes seleccionar un colegio';
        this.mostrarErrores = true;
      }
      if (this.selectedCursos.length === 0) {
        this.errors.cursos = 'Debes seleccionar al menos un curso';
        this.mostrarErrores = true;
      }
      if (!this.inicio || !this.fin || new Date(this.inicio) > new Date(this.fin)) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'Fechas inválidas o incompletas.',
          life: 3000,
        });
        return;
      }
      if (this.mostrarErrores) return;

      this.loading = true;
      try {
        const response = await usuarioService.getInformeTopAlumnosCumplimiento(
          this.inicio,
          this.fin,
          this.selectedColegio,
          this.selectedCursos,
          this.selectedAsignaturas.length > 0 ? this.selectedAsignaturas : null
        );

        if (!response || !response.TopAlumnos || !Array.isArray(response.TopAlumnos)) {
          throw new Error('Respuesta inválida del servidor: datos no disponibles.');
        }

        this.topAlumnos = response.TopAlumnos;
        this.$toast.add({
          severity: 'success',
          summary: 'Éxito',
          detail: response.Mensaje || 'Informe de top 20 alumnos generado con éxito.',
          life: 3000,
        });
      } catch (error) {
        console.error('Error al generar informe de top alumnos:', error);
        this.topAlumnos = [];
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'No se pudo generar el informe.',
          life: 3000,
        });
      } finally {
        this.loading = false;
      }
    },
    exportToExcel() {
      if (!this.topAlumnos.length) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'No hay datos para exportar.',
          life: 3000,
        });
        return;
      }

      // Get colegio, cursos, and asignaturas names
      const colegio = this.colegios.find((c) => c.id_colegio === this.selectedColegio)?.nombre || 'No especificado';
      const cursos = this.selectedCursos
        .map((id) => this.cursos.find((c) => c.id_curso === id)?.nombre || id)
        .join(', ');
      const asignaturas = this.selectedAsignaturas.length > 0
        ? this.selectedAsignaturas
            .map((id) => this.asignaturas.find((a) => a.id_asignatura === id)?.glosa || id)
            .join(', ')
        : 'Todas';

      // Create data for Excel
      const data = [
        ['Top 20 Alumnos por Cumplimiento en Prácticas'],
        [''],
        ['Colegio:', colegio],
        ['Cursos:', cursos],
        ['Asignaturas:', asignaturas],
        ['Fecha Inicio:', this.inicio],
        ['Fecha Fin:', this.fin],
        [''],
        ['ID Usuario', 'Nombre Completo', 'Total Preguntas', 'Correctas', 'Incorrectas', 'Cumplimiento (%)'],
        ...this.topAlumnos.map((item) => [
          item.id_usuario,
          item.nombre_completo,
          item.total_preguntas,
          item.correctas,
          item.incorrectas,
          item.porcentaje_cumplimiento.toFixed(2) + '%',
        ]),
      ];

      // Create worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(data);

      // Apply formatting
      worksheet['!cols'] = [
        { wch: 15 }, // ID Usuario
        { wch: 30 }, // Nombre Completo
        { wch: 15 }, // Total Preguntas
        { wch: 15 }, // Correctas
        { wch: 15 }, // Incorrectas
        { wch: 20 }, // Cumplimiento (%)
      ];
      worksheet['!rows'] = [
        { hpt: 30 }, // Title row
        {}, // Empty row
        {}, // Colegio
        {}, // Cursos
        {}, // Asignaturas
        {}, // Fecha Inicio
        {}, // Fecha Fin
        {}, // Empty row
        { hpt: 20 }, // Header row
      ];

      // Style title
      worksheet['A1'] = {
        ...worksheet['A1'],
        s: {
          font: { bold: true, sz: 16 },
          alignment: { horizontal: 'center', vertical: 'center' },
        },
      };

      // Style headers
      ['A9', 'B9', 'C9', 'D9', 'E9', 'F9'].forEach((cell) => {
        worksheet[cell] = {
          ...worksheet[cell],
          s: {
            font: { bold: true },
            fill: { fgColor: { rgb: 'D3D3D3' } },
            border: {
              top: { style: 'thin' },
              bottom: { style: 'thin' },
              left: { style: 'thin' },
              right: { style: 'thin' },
            },
            alignment: { horizontal: 'center', vertical: 'center' },
          },
        };
      });

      // Style data cells
      for (let row = 10; row < 10 + this.topAlumnos.length; row++) {
        ['A', 'B', 'C', 'D', 'E', 'F'].forEach((col) => {
          const cell = `${col}${row}`;
          worksheet[cell] = {
            ...worksheet[cell],
            s: {
              border: {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' },
              },
              alignment: { horizontal: col === 'B' ? 'left' : 'center', vertical: 'center' },
            },
          };
        });
      }

      // Style metadata cells
      ['A3', 'A4', 'A5', 'A6', 'A7'].forEach((cell, index) => {
        worksheet[cell] = {
          ...worksheet[cell],
          s: {
            font: { bold: true },
            alignment: { horizontal: 'left', vertical: 'center' },
          },
        };
        const valueCell = `B${3 + index}`;
        worksheet[valueCell] = {
          v: data[2 + index][1],
          s: {
            alignment: { horizontal: 'left', vertical: 'center' },
          },
        };
      });

      // Create workbook and export
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'TopAlumnosCumplimiento');
      XLSX.writeFile(workbook, `Top_Alumnos_Cumplimiento_${new Date().toISOString().split('T')[0]}.xlsx`, {
        bookType: 'xlsx',
        type: 'array',
      });

      this.$toast.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Top 20 alumnos exportado a Excel correctamente.',
        life: 3000,
      });
    },
    onCursoChange(newValue) {
      this.selectedCursos = newValue || [];
      this.errors.cursos = ''; // Clear error on change
    },
    onAsignaturaChange(newValue) {
      this.selectedAsignaturas = newValue || [];
      this.errors.asignaturas = ''; // Clear error on change
    },
  },
  mounted() {
    this.loadColegios(); // Load colegios on mount
  },
};
</script>

<style scoped>
.informe-uso-container {
  @apply p-4;
}

.informe-uso-card {
  @apply bg-white rounded-lg shadow p-4;
}

.card-header {
  @apply flex justify-between items-center mb-4;
}

.card-title {
  @apply text-xl font-bold text-gray-800;
}

.form-field {
  @apply mb-4;
}

.form-field label {
  @apply block text-sm font-medium text-gray-700 mb-1;
}

.p-inputtext {
  @apply border-gray-300 rounded-md shadow-sm;
}

:deep(.p-datatable-sm) .p-datatable-thead > tr > th,
:deep(.p-datatable-sm) .p-datatable-tbody > tr > td {
  @apply p-2 text-sm;
}

.flex.justify-end {
  display: flex;
  justify-content: flex-end;
}
</style>